import { useEffect, useState } from 'react';
import { ClockCounterClockwise } from '@phosphor-icons/react';
import { useStore } from '../state/store';
import type { FlightMeta } from '../types';
import { fetchAircraftFlights, fetchCurrentTrack, fetchFlight } from '../api';

/**
 * FR24-style history for the selected airframe: its whole current flight is
 * drawn as soon as it is selected (server-recorded, not just what this tab
 * has seen), and its past flights from the server's 30-day history can be
 * opened one by one on the map.
 */

function formatDay(ts: number): string {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatClock(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function formatDuration(ms: number): string {
  const m = Math.max(0, Math.round(ms / 60_000));
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

export default function FlightHistory({ icao24, flightId }: { icao24: string; flightId: number | null }) {
  const setFocusTrack = useStore((s) => s.setFocusTrack);
  const focusId = useStore((s) => s.focusTrack?.flight.id ?? null);
  const [flights, setFlights] = useState<FlightMeta[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [opening, setOpening] = useState<number | null>(null);

  // Load the live track and the flight list together, as soon as a plane is
  // picked, and again whenever its flight changes under us (it landed and the
  // server started its next leg) so the panel never keeps showing the old one.
  useEffect(() => {
    const ctrl = new AbortController();
    setFlights(null);
    setFailed(false);
    fetchCurrentTrack(icao24, ctrl.signal)
      .then((r) => {
        // Ignore if the user already picked another plane.
        if (!r || useStore.getState().selectedIcao24 !== icao24) return;
        setFocusTrack({ flight: r.flight, points: r.track, live: !r.flight.closed });
      })
      .catch(() => undefined);
    fetchAircraftFlights(icao24, ctrl.signal)
      .then((r) => setFlights(r?.flights ?? []))
      .catch((err) => {
        if ((err as Error).name !== 'AbortError') setFailed(true);
      });
    return () => ctrl.abort();
  }, [icao24, flightId, setFocusTrack]);

  const open = async (f: FlightMeta) => {
    if (f.id === focusId) return;
    setOpening(f.id);
    try {
      if (!f.closed) {
        const r = await fetchCurrentTrack(icao24);
        if (r) setFocusTrack({ flight: r.flight, points: r.track, live: true });
      } else {
        const r = await fetchFlight(f.id);
        if (r) setFocusTrack({ flight: r.flight, points: r.track, live: false });
      }
    } finally {
      setOpening(null);
    }
  };

  return (
    <section className="fp-history" aria-label="Flight history">
      <h3 className="fp-history-title">
        <ClockCounterClockwise size={14} weight="bold" aria-hidden="true" />
        Flight history
      </h3>
      {failed ? (
        <p className="fp-muted">History is unavailable right now.</p>
      ) : flights == null ? (
        <div className="fp-history-loading" aria-busy="true">
          <span className="skeleton skeleton-line" />
          <span className="skeleton skeleton-line" />
        </div>
      ) : flights.length === 0 ? (
        <p className="fp-muted">No flights recorded for this aircraft yet.</p>
      ) : (
        <ul className="board fp-history-list">
          {flights.map((f) => (
            <li key={f.id}>
              <button
                className={f.id === focusId ? 'active' : undefined}
                onClick={() => void open(f)}
                aria-pressed={f.id === focusId}
                aria-busy={opening === f.id}
                title={f.closed ? 'Show this flight on the map' : 'Show the flight in progress'}
              >
                <span className="fh-when">
                  <span>{formatDay(f.startTs)}</span>
                  <span className="mono fh-dim">{formatClock(f.startTs)}</span>
                </span>
                <span className="fh-route mono">
                  {f.origin ?? '----'} → {f.closed ? (f.dest ?? '----') : '…'}
                  {f.callsign && <span className="fh-dim"> {f.callsign}</span>}
                </span>
                <span className="fh-dur">
                  {f.closed ? (
                    <span className="mono">{formatDuration(f.endTs - f.startTs)}</span>
                  ) : (
                    <span className="fh-live">Live</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
